import { createAction } from '@reduxjs/toolkit';
import { RoomWSActions, RoomWSMessages } from 'common/constants';
import { EventRoomApiPayload, EventRoomAttendee, EventRoomAttendeeApiPayload, EventRoomBasicInfo } from 'domain/event';
import { createMeetingWSAsyncAction, createMeetingWSMessage } from 'store/utils';

import { MeetingWSRoomParams, NatteringStartedPayload, RoomSupportJoinedPayload } from '../types';

export const roomExit_WS = createMeetingWSMessage<{ roomId: string }>(RoomWSMessages.RoomExit);
export const joinRoom_WS = createMeetingWSMessage<EventRoomApiPayload>(RoomWSMessages.RoomJoin);
export const addNewRoom_WS = createMeetingWSMessage<EventRoomApiPayload>(RoomWSMessages.RoomOpened);
export const guestJoinedRoom_WS = createMeetingWSMessage<EventRoomAttendeeApiPayload>(RoomWSMessages.GuestJoinedRoom);
export const roomsCountUpdated_WS = createMeetingWSMessage<{ roomsCount: number }>(RoomWSMessages.RoomsCountUpdated);
export const roomSupportRequested_WS = createMeetingWSMessage<{ room: EventRoomApiPayload }>(
  RoomWSMessages.RoomSupportRequested
);
export const roomSupportRequestedList_WS = createMeetingWSMessage<{ rooms: EventRoomApiPayload[] }>(
  RoomWSMessages.RoomSupportRequestedList
);
export const roomSupportCancelled_WS = createMeetingWSMessage<{ roomId: string }>(RoomWSMessages.RoomSupportCancelled);
export const roomSupportJoined_WS = createMeetingWSMessage<RoomSupportJoinedPayload>(RoomWSMessages.RoomSupportJoined);
export const roomSupportLeft_WS = createMeetingWSMessage<RoomSupportJoinedPayload>(RoomWSMessages.RoomSupportLeft);
export const updateRoomAttendee = createMeetingWSMessage<{ roomId: string; attendee: EventRoomAttendee }>(
  RoomWSMessages.UpdateRoomAttendee
);
export const guestDisconnected_WS = createMeetingWSMessage<{ roomId: string; userId: number }>(
  RoomWSMessages.GuestDisconnected
);
export const userDisconnectedList_WS = createMeetingWSMessage<{ userId: number }[]>(
  RoomWSMessages.UserDisconnectedList
);
export const meetingRoomClosed_WS = createMeetingWSMessage<{ id: string }>(RoomWSMessages.RoomClosed);
export const natteringStarted_WS = createMeetingWSMessage<NatteringStartedPayload>(RoomWSMessages.NatteringStarted);
export const meetingBroadcastCountdownStarted_WS = createMeetingWSMessage(RoomWSMessages.BroadcastCountdown);
export const transcriptionStarted_WS = createMeetingWSMessage(RoomWSMessages.TranscriptionStarted);
export const transcriptionStopped_WS = createMeetingWSMessage(RoomWSMessages.TranscriptionStopped);
/** @new Automated Natters only */
export const natterEnded_WS = createMeetingWSMessage(RoomWSMessages.NatterEnded);

export const requestNatterSupport = createMeetingWSAsyncAction<MeetingWSRoomParams>(RoomWSActions.RequestSupport);
export const cancelNatterSupport = createMeetingWSAsyncAction<MeetingWSRoomParams>(RoomWSActions.CancelSupport);
export const leaveRoomAsSupport = createMeetingWSAsyncAction<MeetingWSRoomParams>(RoomWSActions.LeaveAsSupport);
export const joinRoomAsSupport = createMeetingWSAsyncAction<MeetingWSRoomParams<{ room: EventRoomBasicInfo }>>(
  RoomWSActions.JoinAsSupport
);

// [NAT-4608] Create simple Redux action instead of MeetingWS to not send action `room_connection_error` to WS
export const meetingRoomConnectionError = createAction<MeetingWSRoomParams>(RoomWSActions.RoomConnectionError);
export const startTranscription = createMeetingWSAsyncAction<MeetingWSRoomParams>(RoomWSActions.StartTranscription);
export const stopTranscription = createMeetingWSAsyncAction<MeetingWSRoomParams>(RoomWSActions.StopTranscription);

export const natteringActions = {
  joinNatteringModal: {
    accepted: createAction('[Join Nattering Modal] natter accepted'),
    skipped: createAction('[Join Nattering Modal] natter skipped'),
    audioIssueDetected: createAction('[Join Nattering Modal] audio issue detected'),
    automaticallySkippedDueToNetworkConditions: createAction(
      '[Skip Nattering Modal] automatically skipped natter due to network conditions'
    ),
  },
  skipMatchingButton: {
    /** User chose to stop waiting to be matched (Skip option shown after waiting a while). */
    clicked: createAction('[SkipMatchingButton] clicked'),
  },
  connectingCountdown: {
    /** The "found someone → 3,2,1" countdown finished — release the buffered join (mount the room). */
    finished: createAction('[Nattering Connecting] countdown finished'),
  },
  resumeCountdown: {
    /** The "broadcast resumed → 3,2,1" countdown finished — release the buffered broadcast start. */
    finished: createAction('[Nattering Resume] countdown finished'),
  },
  skipConfirm: {
    // The skip leaves the pool immediately (so no match lands while the skip screen plays), but
    // the backend then moves the user to the waiting room and flips the phase. These keep the
    // round experience mounted through the skip screen so navigation to written feedback is held
    // until it has been read and advances on its own.
    opened: createAction('[Nattering Skip] confirmation opened'),
    closed: createAction('[Nattering Skip] confirmation closed'),
  },
};
